import {
  NAV_PLANETS,
  planetPosition,
  planetVisibility,
  type Planet,
} from "@nav-games/astro";

/// Planet visibility panel — for each of the four navigational
/// planets, today's elongation from the Sun, the morning/evening/
/// glare-loss tag, and a one-line narrative about when to look for it.
/// The "planet notes" the RYA syllabus mentions but doesn't ship as
/// printed text.

interface PlanetVisibilityPanelProps {
  date?: Date;
}

const PLANET_LABEL: Record<Planet, string> = {
  venus: "Venus",
  mars: "Mars",
  jupiter: "Jupiter",
  saturn: "Saturn",
};

function VisibilityTag({
  visibility,
}: {
  visibility: ReturnType<typeof planetVisibility>["visibility"];
}) {
  const colour =
    visibility === "Evening star"
      ? "#c0682b"
      : visibility === "Morning star"
        ? "#3b78b5"
        : "#888";
  return (
    <span
      style={{
        background: colour,
        color: "white",
        borderRadius: 4,
        padding: "2px 6px",
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {visibility}
    </span>
  );
}

export function PlanetVisibilityPanel({ date }: PlanetVisibilityPanelProps) {
  const now = date ?? new Date();

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Planet visibility right now</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
        For each of the four navigational planets: elongation from the
        Sun (signed — east is positive, "evening star"), today's
        magnitude estimate, and a one-line note on when and where to
        look. Planets within 10° of the Sun are flagged as "lost in
        the glare" — too close for a useful twilight sight.
      </p>

      {NAV_PLANETS.map((p) => {
        const v = planetVisibility(p, now);
        const pos = planetPosition(p, now);
        return (
          <div
            key={p}
            style={{
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-sm)",
              padding: 10,
              marginBottom: 8,
              background: "var(--bg-elev)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 4,
              }}
            >
              <strong style={{ fontSize: 16 }}>{PLANET_LABEL[p]}</strong>
              <VisibilityTag visibility={v.visibility} />
              <span className="small muted">
                mag {pos.magnitude.toFixed(1)}
              </span>
            </div>
            <div className="small muted" style={{ marginBottom: 4 }}>
              Elongation: {v.elongationDeg >= 0 ? "+" : ""}
              {v.elongationDeg.toFixed(1)}° (E of Sun is +). Transits about{" "}
              {Math.abs(v.meridianOffsetHours).toFixed(1)}h{" "}
              {v.meridianOffsetHours >= 0 ? "after" : "before"} the Sun.
            </div>
            <p className="small" style={{ margin: 0 }}>
              {v.note}
            </p>
          </div>
        );
      })}
    </div>
  );
}
